import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useToast } from '../../providers/ToastProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '../../providers/UserProvider';
import { useMyPermissions } from '../../hooks/useMyPermissions';
import { Avatar } from '../Avatar';
import {
    type ChatActivity,
    type ChatMessage,
    type EditingMessage,
    type EditingMessageAttachment,
    ChatConfigProvider,
    ChatInput,
    FailedFilesWarning,
    MessageBubble,
    OverlayScrollArea,
    PendingMessageBubble,
    SearchStrip,
    ChatApiVersionToggle,
    useChatApiVersion,
    getChatRollout,
    isOdoEmail,
    useChatSubmit,
    useChatSurface,
    usePatientChat
} from '@odo/shared-ui';
import api from '../../api/config';
import odoChatAvatar from '../../assets/icons/odo-chat.svg';
import { ActivityRow } from './ActivityRow';
import { DeleteMessageConfirmModal } from './DeleteMessageConfirmModal';
import { chatDraftKey } from './chatDraftKey';
import { ChatQuickReplies } from '../quick-replies/chat/ChatQuickReplies';
import { UnfilledBlanksConfirmModal } from '../quick-replies/chat/UnfilledBlanksConfirmModal';
import { useUnfilledBlanksGuard } from '../quick-replies/chat/useUnfilledBlanksGuard';
import type { ConversationStatus } from '../../services/conversations';

// Show/hide activity log preference is persisted per-user via localStorage.
// Default is "off" so the chat stays clean for users who don't care; opt-in.
const SHOW_ACTIVITY_KEY = 'fv_chat_show_activity';

const readShowActivity = (): boolean => {
    try {
        return localStorage.getItem(SHOW_ACTIVITY_KEY) === 'true';
    } catch {
        return false;
    }
};

const DELETE_FAILED_MESSAGE = 'Failed to delete message';

type TimelineItem =
    | { kind: 'message'; key: string; createdAt: string; message: ChatMessage; msgIndex: number }
    | { kind: 'activity'; key: string; createdAt: string; activity: ChatActivity };

// Same teal as the Conversations page's internal rows, kept light so the
// internal thread reads as ODO-only without competing with the bubbles.
const MESSAGES_BACKGROUND: Record<ConversationStatus, string> = {
    external: 'bg-white',
    internal: 'bg-[#007E87]/[0.05]'
};

interface ChatTabProps {
    chatType?: ConversationStatus;
}

const ChatTab: React.FC<ChatTabProps> = ({ chatType: chatTypeProp }) => {
    const {
        patientId,
        messages,
        activities,
        activitiesEnabled,
        loading,
        error,
        send,
        edit,
        remove,
        removeAttachment,
        fileUploadProgress,
        receiptsEnabled,
        otherSideLastSeenAt,
        feed,
        uploadInlineImage,
        chatType: providerChatType
    } = usePatientChat();
    // The provider already knows which thread it serves. Reading it here means
    // every internal chat gets the internal colors - the Conversations panel
    // rendered a bare <ChatTab /> and silently fell back to external styling.
    const chatType: ConversationStatus = chatTypeProp ?? providerChatType;
    const fileUrls = feed?.fileUrls;
    const { user } = useUser();
    const manageOdoStaffMessages = useMyPermissions().hasPermission('iam.update');
    const queryClient = useQueryClient();
    // Switching versions must drop what the other one cached: a v1 message id
    // is a Monday update id and a v2 one is a UUID, so edits and deletes would
    // otherwise address rows that do not exist.
    const { setVersion: switchChatApiVersion } = useChatApiVersion(
        React.useCallback(() => {
            void queryClient.invalidateQueries();
        }, [queryClient])
    );
    const { showToast } = useToast();
    // The rollback, for everyone while the rollout allows switching; otherwise
    // ChatApiVersionGuard holds them on the default and the button is not rendered.
    const showApiVersionSwitch = getChatRollout().canSwitch;

    // Activity-log visibility — persisted across sessions via localStorage.
    const [showActivity, setShowActivity] = useState<boolean>(readShowActivity);
    const toggleShowActivity = useCallback(() => {
        setShowActivity((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(SHOW_ACTIVITY_KEY, String(next));
            } catch {
                /* noop */
            }
            return next;
        });
    }, []);

    const timeline = useMemo<TimelineItem[]>(() => {
        const items: TimelineItem[] = messages.map((m, msgIndex) => ({
            kind: 'message',
            key: `m-${m.id}`,
            createdAt: m.createdAt,
            message: m,
            msgIndex
        }));
        if (showActivity) {
            for (const a of activities) {
                items.push({
                    kind: 'activity',
                    key: `a-${a.id}`,
                    createdAt: a.createdAt,
                    activity: a
                });
            }
        }
        items.sort((x, y) => new Date(x.createdAt).getTime() - new Date(y.createdAt).getTime());
        return items;
    }, [messages, activities, showActivity]);

    const surface = useChatSurface({ messages });

    const {
        containerRef,
        listRef,
        bottomRef,
        searchInputRef,
        sending,
        setSending,
        pendingMessage,
        beginPendingMessage,
        markPendingMessageFailed,
        dismissPendingMessage,
        pendingFiles,
        onFileChange,
        onAddFiles,
        onRemoveFile,
        failedFileNames,
        clearFailedFileNames,
        searchQuery,
        setSearchQuery,
        isSearchOpen,
        activeMatchIdx,
        perMessageMatches,
        flatMatchesCount,
        onPrevMatch,
        onNextMatch,
        closeSearch,
        toggleSearch
    } = surface;

    const composerTopRef = useRef<HTMLDivElement>(null);
    const blanksGuard = useUnfilledBlanksGuard();
    const [editingMessage, setEditingMessage] = useState<EditingMessage | null>(null);
    const [messageToDelete, setMessageToDelete] = useState<ChatMessage | null>(null);
    const [deleting, setDeleting] = useState(false);

    const onEditError = useCallback((message: string) => showToast(message, 'error'), [showToast]);

    const onSubmit = useChatSubmit({
        surface,
        send,
        edit,
        editingMessage,
        setEditingMessage,
        attachmentsOnEdit: true,
        onEditError
    });

    const onStartEdit = useCallback((message: ChatMessage) => {
        const isHtml = message.format === 'html' && !!message.htmlBody;
        // Only assets we own (have a patientFileId for) can be removed through
        // the per-file delete endpoint; legacy Monday-only assets are filtered
        // out so they don't render a non-functional X.
        const existingAttachments: EditingMessageAttachment[] = message.assets
            .filter((a) => !!a.patientFileId)
            .map((a) => ({ patientFileId: a.patientFileId!, name: a.name }));
        setEditingMessage({
            id: message.id,
            html: isHtml ? message.htmlBody! : message.text,
            doc: message.bodyDoc,
            existingAttachments: existingAttachments.length > 0 ? existingAttachments : undefined
        });
    }, []);

    const onCancelEdit = useCallback(() => {
        setEditingMessage(null);
    }, []);

    const onRemoveExistingAttachment = useCallback(
        async (patientFileId: string) => {
            if (!editingMessage) return;
            // Optimistically drop the chip from the edit banner. The provider
            // also clears it from `messages[].assets`; if the request fails the
            // provider rolls back the feed and we restore the chip from the
            // refreshed message state on the next render.
            const removedFromEdit = editingMessage.existingAttachments?.find(
                (a) => a.patientFileId === patientFileId
            );
            setEditingMessage((prev) =>
                prev
                    ? {
                          ...prev,
                          existingAttachments: prev.existingAttachments?.filter(
                              (a) => a.patientFileId !== patientFileId
                          )
                      }
                    : prev
            );
            try {
                await removeAttachment?.(editingMessage.id, patientFileId);
            } catch {
                // Restore the chip we just dropped — the feed has already been
                // rolled back by the provider.
                if (removedFromEdit) {
                    setEditingMessage((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  existingAttachments: [
                                      ...(prev.existingAttachments ?? []),
                                      removedFromEdit
                                  ]
                              }
                            : prev
                    );
                }
            }
        },
        [editingMessage, removeAttachment]
    );

    const onRequestDelete = useCallback((message: ChatMessage) => {
        setMessageToDelete(message);
    }, []);

    const onCancelDelete = useCallback(() => {
        if (deleting) return;
        setMessageToDelete(null);
    }, [deleting]);

    const onConfirmDelete = useCallback(async () => {
        if (!messageToDelete || deleting) return;
        try {
            setDeleting(true);
            await remove?.(messageToDelete.id);
            setMessageToDelete(null);
            setEditingMessage((prev) => (prev && prev.id === messageToDelete.id ? null : prev));
        } catch {
            // Provider rolls back the optimistic removal; keep the modal open.
            showToast(DELETE_FAILED_MESSAGE, 'error');
        } finally {
            setDeleting(false);
        }
    }, [messageToDelete, deleting, remove, showToast]);

    const onRetryFailedMessage = useCallback(async () => {
        if (!pendingMessage || !pendingMessage.failed || sending) return;
        const messageText = pendingMessage.text;
        const messageFiles = pendingMessage.files;
        const messageDoc = pendingMessage.doc;
        try {
            setSending(true);
            beginPendingMessage(messageText, messageFiles, messageDoc);
            await send(messageText, messageFiles, messageDoc);
        } catch {
            markPendingMessageFailed();
        } finally {
            setSending(false);
        }
    }, [pendingMessage, sending, send, setSending, beginPendingMessage, markPendingMessageFailed]);

    const chatConfig = useMemo(
        () => ({
            isCurrentUser: (email: string | undefined) => isOdoEmail(email),
            renderAvatar: (props: { user: { name: string; email: string }; imageURL?: string }) => {
                let imgURL: string | undefined = props.imageURL;
                if (!imgURL && props.user.email === user?.email && user?.profilePictureUrl) {
                    imgURL = user.profilePictureUrl;
                }
                if (!imgURL && isOdoEmail(props.user.email)) {
                    imgURL = odoChatAvatar;
                }
                return <Avatar user={props.user} imageURL={imgURL} />;
            },
            filePreviewMode: 'preview' as const,
            editingEnabled: true,
            deletingEnabled: true,
            manageOdoStaffMessages,
            htmlRenderingEnabled: true,
            styleVariant: 'admin' as const,
            apiBaseUrl: api.defaults.baseURL ?? '',
            getAuthToken: () => localStorage.getItem('odo_login_token'),
            // Files embedded in a v2 body are image/chatFile nodes carrying a
            // fileId, never a URL. The feed signs one URL per file; without this
            // DocContent cannot resolve any of them and shows [filename].
            resolveFileUrl: (fileId: string) => fileUrls?.[fileId],
            receiptsEnabled,
            otherSideLastSeenAt
        }),
        [
            user?.email,
            user?.profilePictureUrl,
            manageOdoStaffMessages,
            receiptsEnabled,
            otherSideLastSeenAt,
            fileUrls
        ]
    );

    return (
        <ChatConfigProvider config={chatConfig}>
            <div ref={containerRef} className="flex flex-col h-full overflow-hidden">
                {isSearchOpen && (
                    <SearchStrip
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                        activeMatchIdx={activeMatchIdx}
                        flatMatchesCount={flatMatchesCount}
                        onPrevMatch={onPrevMatch}
                        onNextMatch={onNextMatch}
                        onClose={closeSearch}
                        inputRef={searchInputRef}
                    />
                )}

                {/* Messages */}
                <OverlayScrollArea
                    scrollRef={listRef}
                    className={`flex-1 min-h-0 ${MESSAGES_BACKGROUND[chatType]}`}
                    contentClassName="px-4 py-3"
                    innerClassName={
                        messages.length === 0 && !loading && !pendingMessage
                            ? 'min-h-full flex items-center justify-center'
                            : 'space-y-4'
                    }
                >
                    {messages.length === 0 && loading && (
                        <div className="flex items-center justify-center py-8">
                            <div className="h-5 w-5 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin mr-2" />
                            <div className="text-xs text-gray-500">Loading&hellip;</div>
                        </div>
                    )}
                    {messages.length === 0 && !loading && !pendingMessage && (
                        <div className="text-[#737373] text-sm italic">No messages yet.</div>
                    )}
                    {timeline.map((item) =>
                        item.kind === 'message' ? (
                            <MessageBubble
                                key={item.key}
                                message={item.message}
                                msgIndex={item.msgIndex}
                                perMessageMatches={perMessageMatches}
                                activeMatchIdx={activeMatchIdx}
                                searchQuery={searchQuery}
                                currentUserEmail={user?.email}
                                onStartEdit={onStartEdit}
                                onDeleteMessage={onRequestDelete}
                            />
                        ) : (
                            <ActivityRow key={item.key} activity={item.activity} />
                        )
                    )}

                    {pendingMessage && (
                        <PendingMessageBubble
                            pendingMessage={pendingMessage}
                            fileUploadProgress={fileUploadProgress}
                            sending={sending}
                            onRetry={onRetryFailedMessage}
                            onDismiss={dismissPendingMessage}
                            userName={user?.name}
                            userEmail={user?.email}
                        />
                    )}

                    {failedFileNames.length > 0 && (
                        <FailedFilesWarning
                            fileNames={failedFileNames}
                            onDismiss={clearFailedFileNames}
                        />
                    )}

                    <div ref={bottomRef} />
                    {loading && messages.length > 0 && (
                        <div className="flex items-center justify-center py-2">
                            <div className="h-3 w-3 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin mr-2" />
                            <div className="text-[10px] text-gray-500">Refreshing&hellip;</div>
                        </div>
                    )}
                    {error && <div className="text-xs text-red-500">{error}</div>}
                </OverlayScrollArea>

                {/* Zero-height anchor inset like the composer's content, so the quick replies popover
                    opens above it without touching the panel's borders. */}
                <div ref={composerTopRef} className="mx-3" />
                <ChatInput
                    onSubmit={onSubmit}
                    pendingFiles={pendingFiles}
                    onFileChange={onFileChange}
                    onAddFiles={onAddFiles}
                    onRemoveFile={onRemoveFile}
                    sending={sending}
                    isSearchOpen={isSearchOpen}
                    onToggleSearch={toggleSearch}
                    editingMessage={editingMessage}
                    onCancelEdit={onCancelEdit}
                    onRemoveExistingAttachment={onRemoveExistingAttachment}
                    draftKey={patientId ? chatDraftKey(patientId, providerChatType) : undefined}
                    trailingActions={
                        activitiesEnabled || showApiVersionSwitch ? (
                            <div className="flex items-center gap-1">
                                {showApiVersionSwitch && (
                                    <ChatApiVersionToggle
                                        visible={showApiVersionSwitch}
                                        onChange={switchChatApiVersion}
                                    />
                                )}
                                {activitiesEnabled && (
                                    <button
                                        type="button"
                                        onClick={toggleShowActivity}
                                        aria-pressed={showActivity}
                                        aria-label={
                                            showActivity ? 'Hide activity' : 'Show activity'
                                        }
                                        title={showActivity ? 'Hide activity' : 'Show activity'}
                                        className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${
                                            showActivity
                                                ? 'bg-gray-800 text-white'
                                                : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                                        }`}
                                    >
                                        {showActivity ? (
                                            <EyeIcon className="w-3.5 h-3.5" />
                                        ) : (
                                            <EyeSlashIcon className="w-3.5 h-3.5" />
                                        )}
                                    </button>
                                )}
                            </div>
                        ) : null
                    }
                    renderTools={(insertContent) => (
                        <ChatQuickReplies
                            patientId={patientId}
                            chatType={providerChatType}
                            anchorRef={composerTopRef}
                            onInsert={insertContent}
                        />
                    )}
                    confirmSubmit={blanksGuard.confirmSubmit}
                    onImageUpload={uploadInlineImage}
                />

                {blanksGuard.blanks && (
                    <UnfilledBlanksConfirmModal
                        blanks={blanksGuard.blanks}
                        onSend={() => blanksGuard.answer(true)}
                        onCancel={() => blanksGuard.answer(false)}
                    />
                )}

                {messageToDelete && (
                    <DeleteMessageConfirmModal
                        isDeleting={deleting}
                        onConfirm={onConfirmDelete}
                        onCancel={onCancelDelete}
                    />
                )}
            </div>
        </ChatConfigProvider>
    );
};

export { ChatTab };
